
import * as $ from 'jquery';

import * as CanvasTemplates from 'src/integration/canvas/templates';
import * as CanvasFiles from 'src/integration/canvas/ui/files';
import * as CanvasFilesNew from 'src/integration/canvas/ui/files-new-experience';
import * as CanvasModules from 'src/integration/canvas/ui/modules';
import * as CanvasSyllabus from 'src/integration/canvas/ui/syllabus';
import * as CanvasUserContent from 'src/integration/canvas/ui/userContent';
import * as CanvasUiUtil from 'src/integration/canvas/ui/util';
import { isNewFilesExperience } from 'src/integration/canvas/ui/util';
import * as AllyIntegrationUtil from 'src/integration/util';
import { hasValue } from 'src/shared/NullUtil';
import * as AllyConfig from 'src/shared/rest/Config';
import { FileReference } from 'src/shared/rest/domain/FileReference';
import { enableTooltips } from 'src/shared/tscomponents/tooltip/TooltipUtil';

import { getDiscussionInfo } from './Discussions';

// The page types for which there's a supported Ally integration
const pageTypes = {
    // /course/:courseId/modules - The modules overview page
    'MODULES_LIST': 'modulesList',

    // /courses/:courseId/files/:fileId?module_item_id=:moduleItemId - A single file module preview
    'MODULES_SINGLE_FILE': 'modulesSingleFile',

    // /courses/:courseId/files - The files tool
    'FILES': 'files',

    // /courses/:courseId/files/:fileId - A file preview, but not in a module context
    'FILES_SINGLE_FILE': 'filesSingleFile',

    // /courses/:courseId/syllabus - The course syllabus
    'SYLLABUS': 'syllabus',

    // /courses/:courseId/pages/:pageId - User generated content in a wiki page
    'WIKI_PAGE': 'wikiPage',

    // /courses/:courseId/discussion - A discussion
    'DISCUSSION': 'discussion',

    // /courses/:courseId/assignments/:assignmentId - An assignment
    'ASSIGNMENT': 'assignment',

    // /courses/:courseId/quizzes/:quizId - A quiz
    'QUIZ': 'quiz',

    // /courses/:courseId/external_tools/:toolId -- The Ally LTI tool
    'LTI_ALLY': 'ltiAlly'
};

/**
 * Initialize the file id poller for the active tool
 */
export async function init() {
    // Always show a tooltip above the score indicators
    enableTooltips();

    const pageInfo = await getPageInfo();
    const pageType = (pageInfo && pageInfo.type);
    if (pageType === pageTypes.MODULES_LIST &&
        (window.ENV.MODULE_FILE_PERMISSIONS.manage_files_add ||
            window.ENV.MODULE_FILE_PERMISSIONS.manage_files_delete ||
            window.ENV.MODULE_FILE_PERMISSIONS.manage_files_edit)) {
        // Modules listing
        CanvasModules.initList();
    } else if (pageType === pageTypes.FILES_SINGLE_FILE || pageType === pageTypes.MODULES_SINGLE_FILE) {
        // A single module file download
        CanvasModules.initSingleFileDownload();
    } else if (pageType === pageTypes.FILES) {
        // Files tool
        if (isNewFilesExperience()) {
            // New experience files tool
            CanvasFilesNew.init();
        } else {
            // Old experience files tool
            CanvasFiles.init();
        }
    } else if (pageType === pageTypes.SYLLABUS) {
        // The Course syllabus. This page is similar to regular user content but the poller is required as Canvas
        // doesn't seem to enhance the syllabus content after editing it.
        CanvasSyllabus.init();
        CanvasUserContent.init(pageInfo);
    } else if (pageType === pageTypes.WIKI_PAGE || pageType === pageTypes.DISCUSSION ||
        pageType === pageTypes.ASSIGNMENT || pageType === pageTypes.QUIZ) {
        // User content tools
        CanvasUserContent.init(pageInfo);
    } else if (pageType === pageTypes.LTI_ALLY) {
        $('body').addClass('full-width');
    }
}

// When a user logs out, clear the JWT tokens that got placed in the session storage
// This isn't fool-proof as the session might time out and the tokens could be left
// in the storage. At that point we'll need to rely on the user closing the tab as that
// clears the session storage.
$(document).on('submit', 'form[action="/logout"]', function() {
    const prefix = `ally.jwt.${AllyConfig.clientId()}.`;
    const keys = sessionStorage.length;
    for (let i = keys - 1; i >= 0 ; i--) {
        const key = sessionStorage.key(i);
        if (key.indexOf(prefix) === 0) {
            sessionStorage.removeItem(key);
        }
    }
});

/***********************
 * ACCESSIBLE VERSIONS *
 ***********************/
let $avMenuOpener = null;
document.addEventListener('focusin', function(ev) {
    const $el = $(ev.target).closest('.al-trigger');
    if ($el.length > 0) {
        $avMenuOpener = $el;
    }
}, { 'capture': true });

/**
 * Launch the accessible versions modal
 */
document.addEventListener('click', function(ev) {
    const isNewFiles = isNewFilesExperience();
    const $el = $(ev.target).closest(isNewFiles ? '.ally-accessible-versions-new-files' : '.ally-accessible-versions');
    if ($el.length === 0) return;

    const fileId = $el.attr('data-id');
    const fileReference = new FileReference(fileId);
    const fileInfo = CanvasUiUtil.fileInformation()[fileReference.id()];

    const $allyAccessibleVersionsToolbar = $(isNewFiles ?
        '#ally-accessible-versions-toolbar-new' :
        '#ally-accessible-versions-toolbar');
    const isPreviewPane = $el.is(isNewFiles ? '.ally-accessible-versions-new-files' : '.ef-file-preview-button');
    const parentSelector = isPreviewPane
        ? (isNewFiles ? '.css-8tva9x-modal' : '.ef-file-preview-overlay')
        : null;

    const associatedFiles = getAssociatedFiles($el);
    const opts = {
        associatedFiles,
        parentSelector
    };

    AllyIntegrationUtil.launchAccessibleVersions(AllyConfig.courseId(), fileId, fileInfo, opts, {
        'closed': function () {
            if (isPreviewPane) {
                // In file preview mode, we put focus back on the preview button
                $el.focus();
            } else if ($el[0] === $allyAccessibleVersionsToolbar[0]) {
                // If av modal was opened via Files toolbar, put the focus back on that
                $allyAccessibleVersionsToolbar.focus();
            } else if ($avMenuOpener) {
                // Put the focus back on the menu opener button if the files context menu was used to trigger
                // alternative formats
                $avMenuOpener.focus();
            } else {
                //else return focus to the button that spawned the dialog modal
                $el[0].focus();
            }
        }
    });

    // Prevent bubbling up
    ev.preventDefault();
    ev.stopPropagation();
}, { 'capture': true });

/** Get the asociated files for the given alternative formats launcher */
function getAssociatedFiles($el) {
    if ($el.attr('data-ally-richcontent-eid')) {
        const associatedFiles = [];
        // Note that Canvas assignments embed links to files using relative URLs (e.g. /courses/:courseId/files/...)
        // where pages embed them with a full URL (e.g. https://ally.instructure.com/courses/:courseId/files/...)
        const expectedUrl = `/courses/${AllyConfig.courseId()}/files/`;
        $('.user_content .instructure_file_holder > a').each((i, el) => {
            const $el = $(el);
            // Ignore preview or download buttons
            if ($el.find('img[src="/images/preview.png"]').length > 0) {
                return;
            }

            const url = $(el).attr('href');
            const fileId = $el.attr('data-id');
            const title = ($el.text().trim().length > 0) ? $el.text().trim() : null;
            if (url.includes(expectedUrl) && fileId) {
                associatedFiles.push({
                    'externalId': fileId,
                    'title': title
                });
            }
        });
        return associatedFiles;
    } else {
        return [];
    }
}

/***********************
 * INSTRUCTOR FEEDBACK *
 ***********************/

/**
 * Launch the instructor feedback sidebar
 */
document.addEventListener('click', async function(ev) {
    const $instructorFeedback = $(ev.target).closest('.ally-instructor-feedback');
    if ($instructorFeedback.length === 0) return;

    const fileId = $instructorFeedback.data('ally-file-id');

    // Determine if the user is on a rich content page
    const pageInfo = await getPageInfo();
    const richContentId = (pageInfo.findRichContentId && pageInfo.findRichContentId($instructorFeedback));

    // Build the locator object to determine the location of the embedded image in the document
    const locator = { 'selector': `img[src*="/files/${fileId}/"]` };
    const $img = $instructorFeedback.prev(locator.selector).first();
    $(locator.selector).each(function(i, el) {
        if ($(el).is($img)) {
            locator.index = i;
        }
    });

    // If we support rich content updates for this file, enhance our file reference to indicate it was for an
    // embedded object
    let fileReference = new FileReference(fileId);
    if (richContentId) {
        const allyWorkAroundSourceValues = AllyIntegrationUtil.getAllyWorkAroundSourceValues($img);
        if ($img.length !== 0) {
            fileReference = FileReference.fromElement($img, fileId, allyWorkAroundSourceValues);
        }
    }

    const fileInfo = CanvasUiUtil.fileInformation()[fileReference.id()];
    const filePreviewUrl = CanvasUiUtil.getPreviewUrl(fileInfo);

    // Explicitly blur the link to ensure that further calls to focus it later (i.e., in the following callback)
    // will have an effect
    setTimeout(function() {
        $instructorFeedback.blur();
    });

    // Expresses whether the file is replaced during the IF
    let isReplaced = false;
    let newFile = null;

    // When returning from instructor feedback, re-focus to the element we came from
    let $row = $instructorFeedback.closest('.ef-item-row');
    const closedCallback = function () {
        setTimeout(function () {
            // If the file was replaced with a new one, we have to refresh the page as Canvas can't handle live
            // changes of files
            if (isReplaced) {
                const fileInfo = CanvasUiUtil.fileInformation()[fileReference.id()];
                replaceFile(pageInfo.type, newFile, fileInfo, function () {
                    // If the file was replaced with a new one, we have to refresh the page as Canvas can't
                    // handle live changes of files
                    window.location.reload();
                });
                return;
            }

            // If the row is still visible (i.e., the file has not been deleted), then re-focus the accessibility
            // indicator. Note that we don't use the cached `$instructorFeedback` item because it could be replaced
            // while changing the score in the instructor feedback panel
            if (!$row.is(':visible')) {
                $row = $row
                    .closest('.ef-directory')
                    .find('.ef-item-row')
                    .first();
            }

            $instructorFeedback.focus();
        });
    };
    const callbacks = {
        'closed': closedCallback,
        'deleted': function () {
            window.location.reload();
        },
        'updated': updateFile($instructorFeedback, $img),
        'replacedFile': function (oldFileId, newFileInfo) {
            isReplaced = true;
            newFile = newFileInfo;
        }
    };

    const opts = {
        'filePreviewUrl': filePreviewUrl,
        'locator': locator,
        'richContentId': richContentId
    };
    AllyIntegrationUtil.launchInstructorFeedback(AllyConfig.courseId(), fileReference, opts, callbacks);

    // Prevent bubbling up
    ev.preventDefault();
    ev.stopPropagation();
}, { 'capture': true });

/**
 * Invoked when a file's metadata gets updated through the instructor feedback
 *
 * @param  {jQuery} $instructorFeedback     The link that was previously activated to open instructor feedback
 * @param  {jQuery} [$img]                  The jQuery object holding an embedded image, if applicable. Otherwise,
 *                                          this should be an empty jQuery object
 * @param  {Object} file                    The file to update
 */
function updateFile($instructorFeedback, $img) {
    $img = $img || $();
    return function(file) {
        // Update the alt attribute of the embedded image associated to this file, if applicable. Also apply the
        // update we would expect from the Ally server side when these updates went through
        AllyIntegrationUtil.applyAllyUserUpdate($img, 'alt', AllyIntegrationUtil.getAlternativeText(file));

        // Establish the new file reference for this file. If it's an embedded image, we'll want to build it based
        // on its new alt text
        let fileReference = new FileReference(file.id);
        if ($img.length > 0) {
            fileReference = FileReference.fromElement($img, file.id);
        }

        // Update the locally cached information
        CanvasUiUtil.fileInformation()[fileReference.id()] = file;

        // For embedded references, we show a different kind of icon in Canvas with a circle
        const scoreIndicatorOptions = {
            'circle': fileReference.isEmbedded()
        };

        // Update the indicator. Only update the image source and don't replace the entire indicator. This is
        // necessary as the user content indicator over seizuring images are absolutely positioned and would be lost
        // when replacing the entire link.
        const htmlIndicator = AllyIntegrationUtil.createScoreIndicator(file, scoreIndicatorOptions);
        const imageSource = $('img', htmlIndicator).attr('src');
        $instructorFeedback.find('> img').attr('src', imageSource);

        // TODO: It's possible there's a second instance of the image in the DOM that also has a score indicator
        // which is now out of date. Ideally, that would be updated too. However, in order to update that indicator
        // we need to know in what context we're operating (supported rich content or not) and what type of
        // indicator to insert. As we don't know that, we don't try to update the other score indicators here
    };
}

/**
 * Invoked when a file gets replaced through the instructor feedback
 *
 * @param  {string}      pageType        Current page type
 * @param  {Object}      file            The new file information
 * @param  {Object}      originalFile    The old file information
 * @param  {Function}    callback        Standard callback function
 */
function replaceFile(pageType, file, originalFile, callback) {
    if (pageType === pageTypes.MODULES_LIST || pageType === pageTypes.MODULES_SINGLE_FILE) {
        CanvasModules.fileReplaced(file, originalFile, callback);
    } else {
        return callback();
    }
}

/**
 * Get the current page info.
 *
 * @returns {Object}        pageInfo                            The page info, if applicable
 *          {String}        pageInfo.type                       The type of page, as per the `pageTypes` enumeration
 *          {Function}      [pageInfo.findRichContentId]        A function that determines the rich content id to
 *                                                              which an element belongs. For pages where there are
 *                                                              multiple rich content items shown at once (e.g.,
 *                                                              multiple discussion posts), this is necessary. If
 *                                                              this function returns `null`, it should denote that
 *                                                              the item does not support rich content updates, and
 *                                                              therefore the element should fall back to simple
 *                                                              override_description type of alt text functionality
 *          {jQuery}        [pageInfo.findRichContentId.$el]    The element whose context to inspect to see if it is
 *                                                              associated to a supported rich content id
 *          {String|RegExp} [pageInfo.editorId]                 The editor id matcher to bind an
 *                                                              [[AllyAttributeMonitor]] to for this page
 */
async function getPageInfo() {
    if (window.ENV.MODULE_FILE_PERMISSIONS) {
        return { 'type': pageTypes.MODULES_LIST };
    } else if (isSingleFileModule()) {
        return { 'type': pageTypes.MODULES_SINGLE_FILE };
    } else if (isSingleFileUrl()) {
        return { 'type': pageTypes.FILES_SINGLE_FILE };
    } else if (window.ENV.FILES_CONTEXTS) {
        return { 'type': pageTypes.FILES };
    } else if ($.type(window.ENV.SYLLABUS_BODY) === 'string') {
        return {
            'type': pageTypes.SYLLABUS,
            'editorId': 'course_syllabus_body',
            'findRichContentId': function() {
                // All rich content available on the syllabus page is for just the syllabus
                return 'syllabus';
            },
            'addRichContentAlternativeFormatLauncher': (contentReferenceId) =>
                addAlternativeFormatsIconTo($(`#content h1`).first(), contentReferenceId)
        };
    } else if (window.ENV.PAGE_RIGHTS) {
        return {
            'type': pageTypes.WIKI_PAGE,
            'editorId': ((window.location.pathname === window.ENV.WIKI_PAGE_EDIT_PATH) && 'wiki_page_body'),
            'findRichContentId': function() {
                // All sections of the wiki page area can have its rich content updated
                return `page:${ window.ENV.WIKI_PAGE.page_id}`;
            },
            'addRichContentAlternativeFormatLauncher': function(contentReferenceId) {
                // This could be a page that's displayed as the course home page
                const $courseHomePageH1 = $('#wiki_page_show .course-title');
                const $wikiPageTitleH1 = $('#wiki_page_show h1.page-title');
                if ($courseHomePageH1.length > 0 && $courseHomePageH1.find('[data-ally-content-id]').length === 0) {
                    $courseHomePageH1.append(CanvasTemplates.apply('ally-prominent-icon-rich-content', {
                        'richContentId': contentReferenceId
                    }));
                } else if ($wikiPageTitleH1.length > 0 && $wikiPageTitleH1.find('[data-ally-content-id]').length === 0) {
                    $wikiPageTitleH1.append(CanvasTemplates.apply('ally-prominent-icon-rich-content', {
                        'richContentId': contentReferenceId
                    }));
                }
            }
        };
    } else if (window.ENV.DISCUSSION || window.ENV.DISCUSSION_TOPIC) {
        // In view mode, we access the id through ENV.DISCUSSION.TOPIC.ID, but when editing it's accessed
        // through ENV.DISCUSSION_TOPIC.ATTRIBUTES.id
        const discussionTopicId = (
            window.ENV.DISCUSSION && window.ENV.DISCUSSION.TOPIC && window.ENV.DISCUSSION.TOPIC.ID
        ) || (
            window.ENV.DISCUSSION_TOPIC && window.ENV.DISCUSSION_TOPIC.ATTRIBUTES && window.ENV.DISCUSSION_TOPIC.ATTRIBUTES.id
        ) || window.ENV.discussion_topic_id;

        const { isAnnouncement, permissions = {} } = await getDiscussionInfo(discussionTopicId);

        return {
            'type': pageTypes.DISCUSSION,
            'permissions': permissions,
            'editorId': /discussion-topic-message.*/,
            'findRichContentId': function($el) {
                // We only support rich content updates for the root discussion topic at this time
                // eslint-disable-next-line max-len
                if ($el.parents('#discussion_topic').length > 0 || $el.parents('[data-testid="discussion-topic-container"]').length > 0) {
                    if (isAnnouncement) {
                        return `announcement:${ discussionTopicId}`;
                    } else {
                        return `discussionTopic:${ discussionTopicId}`;
                    }
                } else {
                    return null;
                }
            },
            'addRichContentAlternativeFormatLauncher': (contentReferenceId) =>
                /** first - We provide alternative formats only for the initial post in discussions, we don't track replies */
                addAlternativeFormatsIconTo($(`#content .userMessage`).first().prev(), contentReferenceId)
        };
    } else if (window.ENV.QUIZ) {
        return {
            'type': pageTypes.QUIZ,
            'editorId': 'quiz_description',
            'findRichContentId': function($el) {
                const isQuizPreviewDescription = ($el.parents('#quiz-instructions').length > 0);
                const isQuizProfileDescription = ($el.parents('#quiz_show').length > 0);
                // There are areas where the quiz description rich content (this is the rich content we are able to
                // update) shows on the page along with questions, which are little fragments of rich content.
                // Ensure that for now we only support "embedded rich content updates" for the quiz description
                // elements. Supporting all the question fragments are currently not supported
                if (isQuizPreviewDescription || isQuizProfileDescription) {
                    return `quiz:${ window.ENV.QUIZ.id}`;
                } else {
                    return null;
                }
            },
            'addRichContentAlternativeFormatLauncher': (contentReferenceId) =>
                addAlternativeFormatsIconTo($(`#content h1`).first(), contentReferenceId)
        };
    } else if (window.ENV.ASSIGNMENT_ID || window.ENV.ASSIGNMENT) {
        const assignmentId = window.ENV.ASSIGNMENT_ID || window.ENV.ASSIGNMENT.id;
        return {
            'type': pageTypes.ASSIGNMENT,
            'editorId': 'assignment_description',
            'findRichContentId': function() {
                // All sections of the assignment page area can have its rich content updated
                return `assignment:${ assignmentId}`;
            },
            'addRichContentAlternativeFormatLauncher': (contentReferenceId) => {
                if (hasValue(window.ENV.a2_student_view)) {
                    // In Student View, displaying the AF icon for assignment title.
                    const assignmentTitleTagName = '#content #assignment-student-header-content div';
                    addClassToHtmlElement(assignmentTitleTagName, 'ally-canvas-flex-row');
                    addAlternativeFormatsIconTo($(assignmentTitleTagName).first(), contentReferenceId);
                    addClassToHtmlElement('.ally-accessible-versions', 'ally-canvas-af-flex-row');
                } else {
                    addAlternativeFormatsIconTo($(`#content h1`).first(), contentReferenceId);
                }
            }
        };
    } else if (isAllyLtiTool()) {
        return { 'type': pageTypes.LTI_ALLY };
    } else {
        return null;
    }
}

/**
 * Add the alternative formats icon to the provided element
 *
 * @param {String} elem The element to which the icon should be added
 * @param {String} contentReferenceId The id of the content reference
 */
function addAlternativeFormatsIconTo(elem, contentReferenceId) {
    if (elem.length > 0 && elem.find('[data-ally-content-id]').length === 0) {
        elem.append(CanvasTemplates.apply('ally-prominent-icon-rich-content', {
            'richContentId': contentReferenceId
        }));
    }
}

/**
 * Add the class to the provided element
 *
 * @param {String} tag The header tag
 * @param {String} className
 */
function addClassToHtmlElement(tag, className) {
    const elem = $(`#content ${tag}`).first();
    if (elem.length) {
        elem.first().addClass(className);
    }
}

function isAllyLtiTool() {
    const hasLtiFrame = $('body').hasClass('ic-framed-lti-tool');
    const hasLtiForm = $(`form[action*="${AllyConfig.baseUrl()}"]`).length > 0;
    return hasLtiFrame && hasLtiForm;
}

/**
 * Whether the currently displayed view is a single file module
 *
 * @return {Boolean} `true` if the currently displayed view is a single file module
 */
function isSingleFileModule() {
    return (isSingleFileUrl() && document.location.search.indexOf('module_item_id=') > -1);
}

/**
 * Whether the currently displayed view is a single file preview
 *
 * @return {Boolean} `true` if the currently displayed view is a single file preview
 */
function isSingleFileUrl() {
    const re = /\/courses\/[0-9]+\/files\/[0-9]+/;
    return re.test(document.location.pathname);
}
